import React, { useState, useRef, Component } from 'react';
import { SEOHead } from '../../components/SEOHead';
import { ScrollReveal } from '../../components/ScrollReveal';
import { CheckCircleIcon, CopyIcon, AlertCircleIcon, Wand2Icon, UploadIcon, LinkIcon, XIcon, SendIcon, LoaderIcon, LockIcon } from 'lucide-react';
// Replace this with your actual GHL webhook URL
const GHL_WEBHOOK_URL = 'https://services.leadconnectorhq.com/hooks/JLycxFbUVQFY3z4zPpCS/webhook-trigger/1388343a-44e6-4563-8cd2-2acd1a8562be';
// Define the social networks
const SOCIAL_NETWORKS = [{
  id: 'linkedin',
  name: 'LinkedIn',
  icon: 'https://img.icons8.com/color/48/000000/linkedin.png',
  defaultUrl: 'https://linkedin.com/'
}, {
  id: 'yelp',
  name: 'Yelp',
  icon: 'https://img.icons8.com/color/48/000000/yelp.png',
  defaultUrl: ''
}, {
  id: 'google',
  name: 'Google Business',
  icon: 'https://img.icons8.com/color/48/000000/google-logo.png',
  defaultUrl: ''
}, {
  id: 'instagram',
  name: 'Instagram',
  icon: 'https://img.icons8.com/color/48/000000/instagram-new.png',
  defaultUrl: 'https://instagram.com/'
}, {
  id: 'facebook',
  name: 'Facebook',
  icon: 'https://img.icons8.com/color/48/000000/facebook-new.png',
  defaultUrl: ''
}, {
  id: 'twitter',
  name: 'Twitter / X',
  icon: 'https://img.icons8.com/color/48/000000/twitter--v1.png',
  defaultUrl: ''
}, {
  id: 'youtube',
  name: 'YouTube',
  icon: 'https://img.icons8.com/color/48/000000/youtube-play.png',
  defaultUrl: ''
}, {
  id: 'tiktok',
  name: 'TikTok',
  icon: 'https://img.icons8.com/color/48/000000/tiktok--v1.png',
  defaultUrl: ''
}, {
  id: 'pinterest',
  name: 'Pinterest',
  icon: 'https://img.icons8.com/color/48/000000/pinterest--v1.png',
  defaultUrl: ''
}];
export function GmailSignatureGeneratorPage() {
  // State for all inputs
  const [themeColor, setThemeColor] = useState('#10B981'); // viridia green
  const [logoSize, setLogoSize] = useState(130);
  const [logoUrl, setLogoUrl] = useState('https://images.unsplash.com/photo-1614680376573-3e4e1ef4142a?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80');
  const [personalInfo, setPersonalInfo] = useState({
    name: 'Jane Doe',
    role: 'Marketing Director',
    phone: '(555) 123-4567',
    email: 'hello@yourcompany.com',
    location: 'Phoenix, AZ',
    website: 'www.yourcompany.com'
  });
  // Initialize social links state
  const initialSocials = SOCIAL_NETWORKS.reduce((acc, net) => {
    acc[net.id] = net.defaultUrl;
    return acc;
  }, {} as Record<string, string>);
  const [socialLinks, setSocialLinks] = useState(initialSocials);
  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const signatureRef = useRef<HTMLDivElement>(null);
  // Opt-in gate state
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showOptIn, setShowOptIn] = useState(false);
  const [optInData, setOptInData] = useState({
    name: '',
    email: '',
    businessName: ''
  });
  const [consentChecked, setConsentChecked] = useState(false);
  const [optInSubmitting, setOptInSubmitting] = useState(false);
  const [optInError, setOptInError] = useState('');
  // Handlers
  const handleInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPersonalInfo({
      ...personalInfo,
      [e.target.name]: e.target.value
    });
  };
  const handleSocialChange = (id: string, value: string) => {
    setSocialLinks({
      ...socialLinks,
      [id]: value
    });
  };
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = event => {
        if (event.target?.result) {
          setLogoUrl(event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({
      message,
      type
    });
    setTimeout(() => setToast(null), 3000);
  };
  const copySignature = () => {
    if (!signatureRef.current) return;
    try {
      const range = document.createRange();
      range.selectNode(signatureRef.current);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
        const successful = document.execCommand('copy');
        if (successful) {
          showToast('Success! Signature copied to clipboard.', 'success');
        } else {
          showToast('Failed to copy. Please select it manually.', 'error');
        }
        selection.removeAllRanges();
      }
    } catch (err) {
      showToast('Your browser does not support auto-copying. Please select it manually.', 'error');
    }
  };
  const handleCopyClick = () => {
    if (isUnlocked) {
      copySignature();
    } else {
      setShowOptIn(true);
    }
  };
  const handleOptInSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOptInSubmitting(true);
    setOptInError('');
    try {
      // Use no-cors mode to avoid CORS blocking on GHL webhook
      // With no-cors, we can't read the response, but the POST still reaches GHL
      await fetch(GHL_WEBHOOK_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'text/plain'
        },
        body: JSON.stringify({
          firstName: optInData.name.split(' ')[0],
          lastName: optInData.name.split(' ').slice(1).join(' '),
          email: optInData.email,
          companyName: optInData.businessName,
          source: 'Gmail Signature Generator'
        })
      });
      setIsUnlocked(true);
      setShowOptIn(false);
      // Auto-copy after unlocking
      setTimeout(() => copySignature(), 300);
    } catch {
      // Even if the fetch appears to fail, the data likely reached GHL
      // Unlock anyway so the user isn't stuck
      setIsUnlocked(true);
      setShowOptIn(false);
      setTimeout(() => copySignature(), 300);
    } finally {
      setOptInSubmitting(false);
    }
  };
  // Derived values for the signature
  const hexNoHash = themeColor.replace('#', '');
  const formattedPhone = personalInfo.phone.replace(/[^0-9+]/g, '');
  const formattedWebsite = personalInfo.website.startsWith('http') ? personalInfo.website : `https://${personalInfo.website}`;
  const encodedLocation = encodeURIComponent(personalInfo.location);
  return <div className="bg-viridia-black min-h-screen pt-24 pb-20" data-id="element-2851" data-mp="3mz1aec7pxkbd">
      <SEOHead title="Free Gmail Signature Generator | Viridia Analytics" description="Create a professional, mobile-friendly email signature for Gmail. Customize colors, add your logo, and include social links. 100% free tool." canonical="/tools/gmail-signature-generator" data-id="element-2852" data-mp="3mx83cvt70y2u" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2853" data-mp="26mw0gtz04cz2">
        <ScrollReveal data-id="element-2854" data-mp="m3tk5m49gh4z">
          <header className="mb-12 text-center max-w-3xl mx-auto" data-id="element-2855" data-mp="m4li33lbyqyf">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-4 border border-viridia/20" data-id="element-2856" data-mp="r122pntbmn6r">
              FREE TOOL
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-2857" data-mp="zvqabkmdcgyq">
              Gmail Signature Generator
            </h1>
            <p className="text-gray-400 text-lg" data-id="element-2858" data-mp="14vmbzd7lfwb4">
              Customize your details, then copy and paste directly into your
              Gmail settings. No coding required.
            </p>
          </header>
        </ScrollReveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-2859" data-mp="1itaz6rp4vfjc">
          {/* LEFT COLUMN: CONTROLS */}
          <div className="lg:col-span-5 xl:col-span-4 space-y-6" data-id="element-2860" data-mp="3gjkkcj5xn78w">
            {/* 1. Branding & Logo */}
            <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800" data-id="element-2861" data-mp="3klfmtqzlnqo9">
              <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b border-gray-800" data-id="element-2862" data-mp="31ncf3il19qgj">
                1. Branding & Logo
              </h2>
              <div className="space-y-5" data-id="element-2863" data-mp="2wmyhkuh25ywr">
                <div data-id="element-2864" data-mp="3agzbnax2exqs">
                  <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-2865" data-mp="3af61hln8tve1">
                    Brand Color
                  </label>
                  <div className="flex items-center gap-3" data-id="element-2866" data-mp="36h8bwhpk97a4">
                    <input type="color" value={themeColor} onChange={e => setThemeColor(e.target.value)} className="h-10 w-20 rounded cursor-pointer border-0 bg-transparent p-0" data-id="element-2867" data-mp="2iq2wu4rg2j0v" />
                    <span className="text-xs text-gray-500" data-id="element-2868" data-mp="2mptwyz2v2bi2">
                      Colors your name & icons
                    </span>
                  </div>
                </div>

                <div data-id="element-2869" data-mp="215vix6nboa9">
                  <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-2870" data-mp="1zungl663udc">
                    Logo Size:{' '}
                    <span className="font-bold text-viridia" data-id="element-2871" data-mp="agd88aivlvjp">{logoSize}</span>px
                  </label>
                  <input type="range" min="60" max="250" value={logoSize} onChange={e => setLogoSize(Number(e.target.value))} className="w-full h-2 bg-gray-800 rounded-lg appearance-none cursor-pointer accent-viridia" data-id="element-2872" data-mp="eh5gp1jxbwex" />
                </div>

                <div className="pt-3 border-t border-gray-800" data-id="element-2873" data-mp="y6pny7kd4wku">
                  <label className="block text-sm font-medium text-gray-300 mb-3" data-id="element-2874" data-mp="jiurcg38i82h">
                    Logo Image
                  </label>
                  <div className="space-y-3" data-id="element-2875" data-mp="ofl8z7uotbq0">
                    <div className="relative" data-id="element-2876" data-mp="1c7a40vyw8ean">
                      <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="logo-upload" data-id="element-2877" data-mp="1c5fndtunn8cd" />
                      <label htmlFor="logo-upload" className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-viridia bg-viridia/10 border border-viridia/20 rounded-md cursor-pointer hover:bg-viridia/20 transition-colors" data-id="element-2878" data-mp="2oeywe3tbrjm2">
                        <UploadIcon className="w-4 h-4 mr-2" data-id="element-2879" data-mp="20ococcjblzc2" />
                        Upload Image
                      </label>
                    </div>
                    <div className="text-center text-xs text-gray-500 font-medium uppercase tracking-wider" data-id="element-2880" data-mp="1ropv4e0qvzu3">
                      or
                    </div>
                    <div className="relative" data-id="element-2881" data-mp="1vqb59rlwa00l">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2882" data-mp="1hpqlas48ty5i">
                        <LinkIcon className="h-4 w-4 text-gray-500" data-id="element-2883" data-mp="1mpmowq913nkj" />
                      </div>
                      <input type="text" value={logoUrl} onChange={e => setLogoUrl(e.target.value)} className="w-full pl-10 pr-3 py-2 bg-viridia-black border border-gray-700 rounded-md focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia text-sm text-white" placeholder="Paste image URL here..." data-id="element-2884" data-mp="132rvt2oy5dtf" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Personal Info */}
            <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800" data-id="element-2885" data-mp="1vsicnjhyqtt8">
              <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b border-gray-800" data-id="element-2886" data-mp="1qsm91ld6h4e7">
                2. Personal Info
              </h2>
              <div className="space-y-4" data-id="element-2887" data-mp="24t6ruhj3f07p">
                {Object.entries(personalInfo).map(([key, value]) => <div key={key} data-id="element-2888" data-mp="20p8uab9f1xgr">
                    <label className="block text-sm font-medium text-gray-300 mb-1 capitalize" data-id="element-2889" data-mp="27v1rnkpspvhd">
                      {key}
                    </label>
                    <input type={key === 'email' ? 'email' : 'text'} name={key} value={value} onChange={handleInfoChange} className="w-full px-3 py-2 bg-viridia-black border border-gray-700 rounded-md focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia text-sm text-white" data-id="element-2890" data-mp="2ctwces7bpu44" />
                  </div>)}
              </div>
            </div>

            {/* 3. Social Links */}
            <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800" data-id="element-2891" data-mp="35i0k1j9ggh9g">
              <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b border-gray-800" data-id="element-2892" data-mp="16566x3qsnz85">
                3. Social Links
              </h2>
              <div className="bg-viridia-black/50 p-3 rounded border border-gray-800 mb-4 flex items-start gap-2" data-id="element-2893" data-mp="1b52aj1vkxon6">
                <Wand2Icon className="w-4 h-4 text-viridia flex-shrink-0 mt-0.5" data-id="element-2894" data-mp="3oftx3qs17m55" />
                <p className="text-xs text-gray-400" data-id="element-2895" data-mp="3jn0t554e0hu4">
                  <strong className="text-gray-300" data-id="element-2896" data-mp="3flz39ewd1i4t">Smart Hide:</strong> Leave a
                  link blank to instantly remove its icon from the signature.
                </p>
              </div>
              <div className="space-y-4" data-id="element-2897" data-mp="b80ou6rsao2l">
                {SOCIAL_NETWORKS.map(net => <div key={net.id} data-id="element-2898" data-mp="76fcqnn2qdtg">
                    <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-2899" data-mp="77gpqxnnhadt">
                      {net.name} URL
                    </label>
                    <input type="text" value={socialLinks[net.id]} onChange={e => handleSocialChange(net.id, e.target.value)} className="w-full px-3 py-2 bg-viridia-black border border-gray-700 rounded-md focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia text-sm text-white" placeholder={`https://${net.id}.com/...`} data-id="element-2900" data-mp="295eqjlmctu0" />
                  </div>)}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: PREVIEW & INSTRUCTIONS */}
          <div className="lg:col-span-7 xl:col-span-8 space-y-6" data-id="element-2901" data-mp="5ooszwm8dwiq">
            {/* Live Preview */}
            <div className="bg-viridia-surface rounded-xl p-6 md:p-8 border border-gray-800 sticky top-24" data-id="element-2902" data-mp="te9eam5ux5v6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 pb-4 border-b border-gray-800 gap-4" data-id="element-2903" data-mp="xeju0qjgban8">
                <h2 className="text-xl font-bold text-white" data-id="element-2904" data-mp="oeoc82ve4he8">Live Preview</h2>
                <button onClick={handleCopyClick} className="bg-viridia hover:bg-viridia-dark text-viridia-black font-bold py-2.5 px-6 rounded-lg shadow-lg transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-2905" data-mp="ogrcrgu65sx7">
                  {isUnlocked ? <>
                      <CopyIcon className="w-4 h-4" data-id="element-2906" data-mp="2wmt7ubxaltzn" /> Copy Signature
                    </> : <>
                      <LockIcon className="w-4 h-4" data-id="element-2907" data-mp="3alj6vx3oa3qn" /> Get Your Signature
                    </>}
                </button>
              </div>

              {/* The actual signature container (white background for email context) */}
              <div className="bg-white p-6 md:p-8 rounded-lg border border-gray-200 overflow-x-auto min-h-[250px]" data-id="element-2908" data-mp="2dw21zkciklq0">
                {/* ======================================================= */}
                {/* START OF EMAIL SIGNATURE HTML (Strict Table Layout)     */}
                {/* ======================================================= */}
                <div ref={signatureRef} data-id="element-2909" data-mp="1z52y2mmmilcr">
                  <table cellPadding="0" cellSpacing="0" border={0} style={{
                  background: 'none',
                  border: 'solid 0px transparent',
                  margin: 0,
                  padding: 0,
                  fontFamily: 'Arial, Helvetica, sans-serif'
                }} data-id="element-2910" data-mp="1u82po52ax6ih">
                    <tbody data-id="element-2911" data-mp="csj3opzjh1jj">
                      <tr data-id="element-2912" data-mp="csre8b86vurk">
                        {/* LOGO COLUMN */}
                        <td valign="middle" style={{
                        paddingRight: '20px',
                        textAlign: 'center',
                        verticalAlign: 'middle'
                      }} data-id="element-2913" data-mp="3bc82lwmmj0fz">
                          <a href={formattedWebsite} target="_blank" rel="noopener noreferrer" style={{
                          textDecoration: 'none'
                        }} data-id="element-2914" data-mp="3pcixrbuyv16y">
                            <img src={logoUrl || 'https://images.unsplash.com/photo-1614680376573-3e4e1ef4142a?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'} width={logoSize} alt="Company Logo" style={{
                            border: 'none',
                            maxWidth: `${logoSize}px`,
                            display: 'block',
                            borderRadius: '4px'
                          }} data-id="element-2915" data-mp="2p4lvb0vgz8ts" />
                          </a>
                        </td>

                        {/* DIVIDER LINE */}
                        <td width="2" style={{
                        backgroundColor: '#8c8c8c',
                        width: '2px',
                        padding: 0
                      }} data-id="element-2916" data-mp="3rna1a6nzhc16"></td>

                        {/* INFO COLUMN */}
                        <td valign="middle" style={{
                        paddingLeft: '20px',
                        verticalAlign: 'middle'
                      }} data-id="element-2917" data-mp="2lw5r526c9ptn">
                          {/* Header Text */}
                          <div style={{
                          marginBottom: '8px'
                        }} data-id="element-2918" data-mp="2gxcc35belqrd">
                            <span style={{
                            fontFamily: 'Arial, Helvetica, sans-serif',
                            fontSize: '17px',
                            fontWeight: 'bold',
                            color: themeColor,
                            letterSpacing: '2px',
                            textTransform: 'uppercase'
                          }} data-id="element-2919" data-mp="27v35gqj9ahj9">
                              {personalInfo.name || ' '}
                            </span>
                            <br data-id="element-2920" data-mp="20dwwmb9j3ud6" />
                            <span style={{
                            fontFamily: 'Arial, Helvetica, sans-serif',
                            fontSize: '13px',
                            fontStyle: 'italic',
                            color: '#666666',
                            letterSpacing: '0.5px'
                          }} data-id="element-2921" data-mp="1vl3p5fmoe7xe">
                              {personalInfo.role || ' '}
                            </span>
                          </div>

                          {/* Contact Links Table */}
                          <table cellPadding="0" cellSpacing="0" border={0} style={{
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          fontSize: '12px',
                          color: '#444444',
                          lineHeight: '20px'
                        }} data-id="element-2922" data-mp="jyltb1l5gyzc">
                            <tbody data-id="element-2923" data-mp="1u4b7hhtcmk28">
                              {/* Phone */}
                              {personalInfo.phone && <tr data-id="element-2924" data-mp="1p69pl07nsqsc">
                                  <td valign="middle" style={{
                                paddingRight: '8px',
                                paddingBottom: '3px'
                              }} data-id="element-2925" data-mp="1p7b79nj6kbj1">
                                    <img src={`https://img.icons8.com/material-rounded/24/${hexNoHash}/phone--v1.png`} alt="Phone" width="16" style={{
                                  display: 'block',
                                  border: 'none'
                                }} data-id="element-2926" data-mp="3aitaa89d3m6d" />
                                  </td>
                                  <td valign="middle" style={{
                                paddingBottom: '3px'
                              }} data-id="element-2927" data-mp="4oh3g6gnhizm">
                                    <a href={`tel:${formattedPhone}`} style={{
                                  color: '#444444',
                                  textDecoration: 'none',
                                  fontWeight: 600
                                }} data-id="element-2928" data-mp="3w1boy64jcukm">
                                      {personalInfo.phone}
                                    </a>
                                  </td>
                                </tr>}

                              {/* Email */}
                              {personalInfo.email && <tr data-id="element-2929" data-mp="jawjl3kd4n9a">
                                  <td valign="middle" style={{
                                paddingRight: '8px',
                                paddingBottom: '3px'
                              }} data-id="element-2930" data-mp="jbzcti55r1sd">
                                    <img src={`https://img.icons8.com/material-rounded/24/${hexNoHash}/mail.png`} alt="Email" width="16" style={{
                                  display: 'block',
                                  border: 'none'
                                }} data-id="element-2931" data-mp="j3qza97h423" />
                                  </td>
                                  <td valign="middle" style={{
                                paddingBottom: '3px'
                              }} data-id="element-2932" data-mp="cl1pb125vu2">
                                    <a href={`mailto:${personalInfo.email}`} style={{
                                  color: '#444444',
                                  textDecoration: 'none',
                                  fontWeight: 600
                                }} data-id="element-2933" data-mp="38tpjnj1d42z2">
                                      {personalInfo.email}
                                    </a>
                                  </td>
                                </tr>}

                              {/* Location */}
                              {personalInfo.location && <tr data-id="element-2934" data-mp="vly128ttxvrn">
                                  <td valign="middle" style={{
                                paddingRight: '8px',
                                paddingBottom: '3px'
                              }} data-id="element-2935" data-mp="vkn16k3i985t">
                                    <img src={`https://img.icons8.com/material-rounded/24/${hexNoHash}/marker.png`} alt="Location" width="16" style={{
                                  display: 'block',
                                  border: 'none'
                                }} data-id="element-2936" data-mp="1ecpilxnz0sh4" />
                                  </td>
                                  <td valign="middle" style={{
                                paddingBottom: '3px',
                                fontWeight: 600
                              }} data-id="element-2937" data-mp="1lvytjvc4kz8a">
                                    <a href={`https://maps.google.com/?q=${encodedLocation}`} target="_blank" rel="noopener noreferrer" style={{
                                  color: '#444444',
                                  textDecoration: 'none'
                                }} data-id="element-2938" data-mp="1ht23jjk9a0ub">
                                      {personalInfo.location}
                                    </a>
                                  </td>
                                </tr>}

                              {/* Website */}
                              {personalInfo.website && <tr data-id="element-2939" data-mp="2mwehwgy79f19">
                                  <td valign="middle" style={{
                                paddingRight: '8px',
                                paddingBottom: '8px'
                              }} data-id="element-2940" data-mp="3jkkeqhovr34z">
                                    <img src={`https://img.icons8.com/material-rounded/24/${hexNoHash}/domain.png`} alt="Website" width="16" style={{
                                  display: 'block',
                                  border: 'none'
                                }} data-id="element-2941" data-mp="35se9vrr6giku" />
                                  </td>
                                  <td valign="middle" style={{
                                paddingBottom: '8px'
                              }} data-id="element-2942" data-mp="1t4grtljupr5r">
                                    <a href={formattedWebsite} target="_blank" rel="noopener noreferrer" style={{
                                  color: '#0000ee',
                                  textDecoration: 'underline',
                                  fontWeight: 600
                                }} data-id="element-2943" data-mp="27xk1z7v452mg">
                                      {personalInfo.website}
                                    </a>
                                  </td>
                                </tr>}
                            </tbody>
                          </table>

                          {/* Social Media Icons Table */}
                          <table cellPadding="0" cellSpacing="0" border={0} style={{
                          marginTop: '4px'
                        }} data-id="element-2944" data-mp="1ivg3qp1du5hw">
                            <tbody data-id="element-2945" data-mp="1mwrvb03th0sq">
                              <tr data-id="element-2946" data-mp="1mwjeix4kpbc9">
                                {SOCIAL_NETWORKS.map(net => {
                                const url = socialLinks[net.id];
                                if (!url || url.trim() === '') return null;
                                return <td key={net.id} style={{
                                  paddingRight: '12px'
                                }} data-id="element-2947" data-mp="l15938k0wpr7">
                                      <a href={url} target="_blank" rel="noopener noreferrer" data-id="element-2948" data-mp="g4ewwwbcaeb2">
                                        <img src={net.icon} alt={net.name} width="22" style={{
                                      display: 'block',
                                      border: 'none'
                                    }} data-id="element-2949" data-mp="tv1v8nd6u26m" />
                                      </a>
                                    </td>;
                              })}
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {/* ======================================================= */}
                {/* END OF EMAIL SIGNATURE HTML                             */}
                {/* ======================================================= */}
              </div>

              {/* Instructions */}
              <div className="mt-8 bg-viridia-black/50 border-l-4 border-viridia p-5 rounded-r-lg" data-id="element-2950" data-mp="2c5s7ioqtsooq">
                <h3 className="font-bold text-white mb-3" data-id="element-2951" data-mp="1eoti1e4azrct">
                  How to install in Gmail:
                </h3>
                <ol className="list-decimal ml-5 space-y-2 text-gray-400 text-sm" data-id="element-2952" data-mp="1jmuvnjiymygw">
                  <li data-id="element-2953" data-mp="1c4of43rc7cae">
                    Ensure all your links are correct in the inputs on the left.
                  </li>
                  <li data-id="element-2954" data-mp="17492xfiskau0">
                    Click the{' '}
                    <strong className="text-gray-300" data-id="element-2955" data-mp="125pg7cwdiv0l">"Copy Signature"</strong>{' '}
                    button above.
                  </li>
                  <li data-id="element-2956" data-mp="xaa3ioa3r55r">
                    Open Gmail, click the{' '}
                    <strong className="text-gray-300" data-id="element-2957" data-mp="t7vjw1ym01df">
                      Settings (Gear) icon
                    </strong>
                    , and click{' '}
                    <strong className="text-gray-300" data-id="element-2958" data-mp="jfzj2j551x5i">
                      "See all settings"
                    </strong>
                    .
                  </li>
                  <li data-id="element-2959" data-mp="eflfbdakl9da">
                    Scroll down to the{' '}
                    <strong className="text-gray-300" data-id="element-2960" data-mp="9htiq5yzcz2r">Signature</strong>{' '}
                    section. Create a new one or edit an existing one.
                  </li>
                  <li data-id="element-2961" data-mp="3r0qzh4ugik36">
                    Click into the text box and press{' '}
                    <strong className="text-gray-300" data-id="element-2962" data-mp="3w0mvohwwx786">Ctrl+V</strong> (Windows)
                    or <strong className="text-gray-300" data-id="element-2963" data-mp="3i0c5lh3ffwo2">Cmd+V</strong> (Mac) to
                    paste.
                  </li>
                  <li data-id="element-2964" data-mp="3myne9ohzk14p">
                    Scroll to the very bottom of the Gmail settings page and
                    click{' '}
                    <strong className="text-gray-300" data-id="element-2965" data-mp="ek5ba16wjbry">Save Changes</strong>.
                  </li>
                </ol>
              </div>

              <div className="mt-6 text-center text-xs text-gray-600" data-id="element-2966" data-mp="4ttuhe24d83n">
                Social media and contact icons provided by{' '}
                <a href="https://icons8.com" target="_blank" rel="noopener noreferrer" className="text-viridia hover:underline" data-id="element-2967" data-mp="9rveq2ba7dgp">
                  Icons8
                </a>
                .
              </div>
            </div>

            {/* Opt-In Modal */}
            {showOptIn && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-2968" data-mp="17my2mmyrmeui">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowOptIn(false)} data-id="element-2969" data-mp="1ldjkq9geakrc"></div>
                <div className="relative bg-viridia-surface border border-gray-800 rounded-2xl p-8 max-w-md w-full shadow-2xl" data-id="element-2970" data-mp="1ggt4nm4lbp5x">
                  <button onClick={() => setShowOptIn(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors" aria-label="Close" data-id="element-2971" data-mp="ontwpn2it172">
                    <XIcon className="w-5 h-5" data-id="element-2972" data-mp="xmnon04wp3ea" />
                  </button>

                  <div className="text-center mb-6" data-id="element-2973" data-mp="2e1fgnby548n2">
                    <div className="w-14 h-14 rounded-full bg-viridia/20 flex items-center justify-center mx-auto mb-4" data-id="element-2974" data-mp="2e4bbgreoyh7a">
                      <Wand2Icon className="w-7 h-7 text-viridia" data-id="element-2975" data-mp="imtk1gsbxyv9" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-2976" data-mp="nl4sbn2z81bd">
                      Your Signature is Ready!
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-2977" data-mp="sjodhl9qx92o">
                      Enter your info below to copy your signature. We'll also
                      send you a backup copy via email.
                    </p>
                  </div>

                  <form onSubmit={handleOptInSubmit} className="space-y-4" data-id="element-2978" data-mp="16k906sis7p1g">
                    <div data-id="element-2979" data-mp="16iebd34e3cs3">
                      <label htmlFor="optin-name" className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-2980" data-mp="1baz37z3qjfsz">
                        Full Name *
                      </label>
                      <input type="text" id="optin-name" required value={optInData.name} onChange={e => setOptInData(prev => ({
                    ...prev,
                    name: e.target.value
                  }))} placeholder="John Smith" className="w-full px-4 py-3 bg-viridia-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia transition-colors" data-id="element-2981" data-mp="1kcyleuqhkpxi" />
                    </div>
                    <div data-id="element-2982" data-mp="3jqyvmzw0xjas">
                      <label htmlFor="optin-email" className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-2983" data-mp="3jqffnopxp4ze">
                        Email Address *
                      </label>
                      <input type="email" id="optin-email" required value={optInData.email} onChange={e => setOptInData(prev => ({
                    ...prev,
                    email: e.target.value
                  }))} placeholder="john@example.com" className="w-full px-4 py-3 bg-viridia-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia transition-colors" data-id="element-2984" data-mp="g3yj36pc7c4r" />
                    </div>
                    <div data-id="element-2985" data-mp="2gzotl4st7mr">
                      <label htmlFor="optin-business" className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-2986" data-mp="2hsqw8stb8yk">
                        Business Name
                      </label>
                      <input type="text" id="optin-business" value={optInData.businessName} onChange={e => setOptInData(prev => ({
                    ...prev,
                    businessName: e.target.value
                  }))} placeholder="Your Company LLC" className="w-full px-4 py-3 bg-viridia-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia transition-colors" data-id="element-2987" data-mp="20wciqr1zr82f" />
                    </div>

                    <div className="flex items-start gap-3" data-id="element-2988" data-mp="hwmouq32q2jn">
                      <input type="checkbox" id="optin-consent" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="mt-1 w-4 h-4 rounded border-gray-600 bg-viridia-black text-viridia focus:ring-viridia focus:ring-offset-0 cursor-pointer flex-shrink-0" data-id="element-2989" data-mp="cxjvw40dbms1" />
                      <label htmlFor="optin-consent" className="text-xs text-gray-400 cursor-pointer leading-relaxed" data-id="element-2990" data-mp="1xu13e5vcxwz6">
                        I agree to receive marketing and promotional emails from
                        Viridia Analytics LLC, including product updates, tips,
                        and special offers. You can unsubscribe at any time.
                        View our{' '}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-viridia hover:underline" data-id="element-2991" data-mp="1jv1h34vbhu66">
                          Privacy Policy
                        </a>
                        .
                      </label>
                    </div>

                    {optInError && <p className="text-red-400 text-sm text-center" data-id="element-2992" data-mp="olphz26dd1lo">
                        {optInError}
                      </p>}

                    <button type="submit" disabled={optInSubmitting || !consentChecked} className="w-full flex items-center justify-center gap-2 px-6 py-3.5 text-base font-bold text-viridia-black bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2993" data-mp="ew61u4a1jlui">
                      {optInSubmitting ? <>
                          <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-2994" data-mp="3n5ej8fgxfcla" />{' '}
                          Unlocking...
                        </> : <>
                          <CopyIcon className="w-5 h-5" data-id="element-2995" data-mp="1le1h1y6cfisx" /> Copy My Signature
                        </>}
                    </button>
                  </form>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && <div className="fixed bottom-5 right-5 z-50 flex flex-col gap-2 animate-in slide-in-from-bottom-5 fade-in duration-300" data-id="element-2996" data-mp="20vjkwhe7n89f">
          <div className={`flex items-center p-4 rounded-lg shadow-lg border-l-4 ${toast.type === 'success' ? 'bg-viridia-surface border-viridia text-white' : 'bg-red-900/50 border-red-500 text-white'}`} data-id="element-2997" data-mp="2thuwso79ovlj">
            {toast.type === 'success' ? <CheckCircleIcon className="w-5 h-5 text-viridia mr-3" data-id="element-2998" data-mp="2jq8anwte5c40" /> : <AlertCircleIcon className="w-5 h-5 text-red-500 mr-3" data-id="element-2999" data-mp="2esyfs1n8g88s" />}
            <div className="font-medium text-sm" data-id="element-3000" data-mp="3ca6wf36jdirz">{toast.message}</div>
          </div>
        </div>}
    </div>;
}